import type { JSX } from 'react'
import type { IRowContainerProps, ISnapAlign } from './RowContainer.h'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ButtonCorners } from 'atoms/Button'
import { CARDS_ASPECT_RATIO } from 'blocks/Cards/Card.h'
import { useClientResolution } from 'utils/hooks'
import { FIXED_INDENTS, LAYOUT, ROW_GAP } from 'vars/index'
import { NextControl, PrevControl, RowTitle } from './components'
import { useRowScrollHandlers, useVisibleCards } from './hooks'
import {
  CarouselScrollableComponentSC,
  CarouselWrapperControlSC,
  CarouselWrapperSC,
  RowContainerSC,
} from './NewRowContainer.styled'
import {
  CardsInRow,
  RowContainerVerticalGaps,
  RowControlsType,
  RowDescriptionsMap,
  ScrollAligning,
  ScrollBehavior,
  SnapAlign,
} from './RowContainer.h'

export const RowContainer = ({
  children,

  itemCount,
  isMoreThanOnePage,
  onSeeAllClick,
  buttonTitle,
  selectedItem,
  disableScrollOnSelectItem,

  cardsInRow = CardsInRow.Horizontal_5,
  cellsAlignment,
  scrollByCardsCount,
  gap = ROW_GAP.SIZE_10,

  horizontalPaddings = LAYOUT.HorizontalPaddings,
  verticalPaddingTop = FIXED_INDENTS.INDENT_0,
  verticalPaddingBottom = FIXED_INDENTS.INDENT_0,
  verticalMarginTop = FIXED_INDENTS.INDENT_0,
  verticalMarginBottom = FIXED_INDENTS.INDENT_0,
  title,
  withSpecialCell,
  descriptionType,
  description,
  backgroundStyle,
  rowPosterImage,

  cardAspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_TRIA_DEFAULT,
  cardAspectRatioVar,
  tvChannelCard = false,
  controlsType,
  titleColor,
  containerVerticalGap,
  withCardDescription = false,
  matchPosterHeight = false,
  cornersButtonLeft = ButtonCorners.CORNERS_RIGHT_25,
  cornersButtonRight = ButtonCorners.CORNERS_LEFT_25,
}: IRowContainerProps): JSX.Element => {
  const rowRef = useRef<HTMLDivElement>(null)

  const visibleCardsCount = useMemo(
    () => +cardsInRow.split('_')[1],
    [cardsInRow]
  )

  const { width: clientWidth } = useClientResolution()
  const rowDirection = useMemo(() => cardsInRow?.charAt(0), [cardsInRow])

  const visibleCards = useVisibleCards({
    clientWidth,
    cardsInRow: visibleCardsCount,
    rowDirection,
    tvChannelCard,
    withSpecialCell,
  })

  const [activeItem, setActiveItem] = useState<number | undefined>()
  const [childrenCount, setChildrenCount] = useState<number>()
  const [groupCells, setGroupCells] = useState<number>(
    scrollByCardsCount || visibleCards
  )

  const [cardWidth, setCardWidth] = useState<number>()
  const [gapBetweenCards, setGapBetweenCards] = useState<number>()
  const [rowOffset, setRowOffset] = useState<number>()
  const [snapAlign, setSnapAlign] = useState<ISnapAlign>(SnapAlign.unset)

  //TODO: Fixes bug FP-2112. You need to look for a solution through css properties.
  useEffect(() => {
    setSnapAlign(SnapAlign.end)
  }, [])

  const isRenderSpecialCell = useMemo(
    () => Boolean(clientWidth >= 600 && withSpecialCell && descriptionType),
    [withSpecialCell, clientWidth, descriptionType, withSpecialCell]
  )

  const controlsInHeader = useMemo(
    () => controlsType === RowControlsType.inHeader,
    [controlsType]
  )

  const Description = RowDescriptionsMap[descriptionType!]

  const extraCount = useMemo(() => {
    let extraCountWithSpecial = 1
    if (visibleCardsCount > 3 && clientWidth >= 768) extraCountWithSpecial = 2
    if (visibleCardsCount > 5) extraCountWithSpecial = 3

    if (isRenderSpecialCell) return extraCountWithSpecial
    return 0
  }, [
    visibleCards,
    isRenderSpecialCell,
    clientWidth,
    withSpecialCell,
    descriptionType,
  ])

  useEffect(() => {
    if (!activeItem || activeItem < 0)
      setActiveItem((_prev) => visibleCards - (extraCount! || 1))
    setChildrenCount((_prev) => itemCount! + (isRenderSpecialCell ? 1 : 0))
  }, [
    visibleCards,
    isRenderSpecialCell,
    itemCount,
    extraCount,
    withSpecialCell,
  ])

  const [isNextControlVisible, setIsNextControlVisible] = useState<boolean>()
  const [isPrevControlVisible, setIsPrevControlVisible] = useState<boolean>()

  const updateControlsVisibility = () => {
    const nextControlVisiblity = activeItem! < childrenCount! - 1
    setIsNextControlVisible(nextControlVisiblity)
    const prevControlVisiblity = activeItem! > visibleCards - (extraCount || 1)
    setIsPrevControlVisible(prevControlVisiblity)
  }
  useLayoutEffect(() => {
    if (!scrollByCardsCount) setGroupCells(visibleCards)
  }, [visibleCards])

  useLayoutEffect(() => {
    if (rowRef.current) {
      const firstChild = rowRef.current!.children[0]
      const secondChild = rowRef.current!.children[1]
      const rowChild = isRenderSpecialCell ? secondChild : firstChild
      const cardWidth = rowChild?.getBoundingClientRect()?.width || 0
      const gapBetweenCards =
        firstChild && secondChild
          ? secondChild.getBoundingClientRect().x -
            firstChild.getBoundingClientRect().width -
            firstChild.getBoundingClientRect().x
          : 0
      const rowOffset =
        firstChild && secondChild
          ? secondChild.getBoundingClientRect().x -
            firstChild.getBoundingClientRect().width -
            gapBetweenCards
          : 0
      setCardWidth(cardWidth)
      setRowOffset(rowOffset)
      setGapBetweenCards(gapBetweenCards)
      updateControlsVisibility()
    }
  }, [isRenderSpecialCell, updateControlsVisibility])

  const [rowScrollPosition, setRowScrollPosition] = useState<number>(0)
  const { next, prev, handleScroll, scrollToItem } = useRowScrollHandlers({
    ref: rowRef,
    currentIndex: activeItem!,
    setCurrentIndex: setActiveItem,
    visibleCards: visibleCards!,
    groupCells,
    childrenCount: childrenCount!,
    gapBetweenCards: gapBetweenCards!,
    extraCount,
    itemSize: cardWidth!,
    rowOffset: rowOffset!,
    setRowScrollPosition,
  })

  useEffect(() => {
    const curIndex =
      Math.round(rowScrollPosition / (cardWidth! + gapBetweenCards!)) -
      (extraCount ? extraCount - 1 : 0) +
      visibleCards -
      1

    setActiveItem(curIndex)
  }, [rowScrollPosition, cardWidth, visibleCards, clientWidth])

  // note that current realisation is suitable only for cases where 1 row at page (not categories pages)
  useLayoutEffect(() => {
    if (selectedItem !== undefined) {
      scrollToItem({
        nextItemIndex: selectedItem,
        // need to scroll catchups row to selelcted item on init page
        behavior: ScrollBehavior.auto,
        inline: ScrollAligning.center,
        block: ScrollAligning.center, //ONLY FOR CATCHUPS HOTFIX (need fixes after saving active position will be done)
      })
    }
  }, [])

  useLayoutEffect(() => {
    if (selectedItem !== undefined && !disableScrollOnSelectItem) {
      scrollToItem({
        nextItemIndex: selectedItem,
        // need avoid catchups row scrolling after selecting active catchup
        behavior: ScrollBehavior.auto,
        inline: ScrollAligning.nearest,
        block: ScrollAligning.center, //ONLY FOR CATCHUPS HOTFIX (need fixes after saving active position will be done)
      })
    }
  }, [selectedItem, disableScrollOnSelectItem])

  useEffect(() => {
    if (itemCount) {
      updateControlsVisibility()
    }
  }, [itemCount, activeItem])

  const isRenderDescriptionInHeader: boolean = useMemo(() => {
    return Boolean(
      (withSpecialCell && !isRenderSpecialCell) ||
      (!withSpecialCell && descriptionType)
    )
  }, [descriptionType, withSpecialCell, isRenderSpecialCell])

  const isRenderHeader: boolean = useMemo(() => {
    return controlsInHeader || isRenderDescriptionInHeader || !descriptionType
  }, [isRenderSpecialCell, controlsInHeader])

  const isRenderSpecialTitle =
    Boolean(isRenderSpecialCell) &&
    descriptionType &&
    Boolean(title || description)

  return itemCount && visibleCards ? (
    <RowContainerSC
      horizontalPaddings={horizontalPaddings}
      verticalPaddingTop={verticalPaddingTop}
      verticalMarginTop={verticalMarginTop}
      verticalMarginBottom={verticalMarginBottom}
      verticalPaddingBottom={verticalPaddingBottom}
      containerVerticalGap={
        isRenderHeader
          ? containerVerticalGap
          : RowContainerVerticalGaps.RowWithoutHeader
      }
      backgroundStyle={backgroundStyle}
      rowPosterImage={rowPosterImage}
      isRenderSpecialCell={Boolean(descriptionType)}
      matchPosterHeight={matchPosterHeight}
      withCardDescription={withCardDescription}
    >
      {isRenderHeader && (
        <RowTitle
          title={title}
          descriptionType={descriptionType}
          currentIndex={activeItem}
          prev={prev}
          next={next}
          nextButtonVisible={isNextControlVisible!}
          controlsType={controlsType}
          withSpecialCell={isRenderSpecialCell}
          description={description}
          titleColor={titleColor!}
          buttonTitle={buttonTitle}
          onSeeAllClick={onSeeAllClick}
          isMoreThanOnePage={isMoreThanOnePage}
        >
          {isRenderDescriptionInHeader && (
            <Description
              isButtonShown={isMoreThanOnePage}
              description={description}
              onSeeAllClick={onSeeAllClick}
              title={title}
              buttonTitle={buttonTitle}
              paddings={[FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings]}
            />
          )}
        </RowTitle>
      )}

      <CarouselWrapperSC
        gap={gap}
        visibleCards={visibleCards}
        cardAspectRatio={cardAspectRatio}
        cardAspectRatioVar={cardAspectRatioVar}
        matchPosterHeight={matchPosterHeight}
      >
        {isPrevControlVisible && !controlsInHeader && (
          <CarouselWrapperControlSC>
            <PrevControl
              controlsType={controlsType!}
              onClick={prev}
              corners={cornersButtonLeft}
            />
          </CarouselWrapperControlSC>
        )}
        <CarouselScrollableComponentSC
          gap={gap}
          ref={rowRef}
          withSpecialCell={isRenderSpecialCell}
          visibleCards={visibleCards}
          extraCount={extraCount}
          cellsAlignment={cellsAlignment}
          cardAspectRatio={cardAspectRatio}
          cardAspectRatioVar={cardAspectRatioVar}
          onScroll={handleScroll}
          withCardDescription={withCardDescription}
          snapAlign={snapAlign}
        >
          {isRenderSpecialTitle && (
            <Description
              description={description}
              onSeeAllClick={onSeeAllClick}
              title={title}
              buttonTitle={buttonTitle}
              isButtonShown={isMoreThanOnePage}
              paddings={[FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings]}
            />
          )}
          {children}
        </CarouselScrollableComponentSC>
        {isNextControlVisible && !controlsInHeader && (
          <CarouselWrapperControlSC>
            <NextControl
              controlsType={controlsType!}
              onClick={next}
              corners={cornersButtonRight}
            />
          </CarouselWrapperControlSC>
        )}
      </CarouselWrapperSC>
    </RowContainerSC>
  ) : (
    <></>
  )
}
